/**
 * The New contest view model (ISSUE-1042). The Commissioner creates the next
 * contest from the pool (Q-777-A), and a future contest can be created while
 * another runs as long as the dates do not overlap (SUR-34).
 *
 * Pure: no React, no network. Every string the New contest dialog shows lives
 * here, plus the name check, which contest the prefill copies, the initial
 * draft, the missing list, the dirty check, the request body and the gate.
 */
import {
  countInclusiveDays,
  DEFAULT_SURVIVOR_CONTEST_DAYS,
  addProviderDays,
  VIEWER_CONTEST_STATUS_CALLED_OFF,
} from "@sportsmet/contest-engine";
import type { useContestList } from "@sportsmet/contest-sdk/private";

import {
  addUtcDays,
  buildContestConfig,
  defaultContestDateRange,
  emptySurvivorRulesDraft,
  missingSurvivorRulesItems,
  nextContestDateRange,
  rulesDraftFromContestConfig,
  type typeSurvivorContestConfig,
  type typeSurvivorRulesDraft,
} from "../createPool/survivorRulesDraft";
import type { typeSeasonListItem } from "../poolSeason/seasonCardModel";

export const NEW_CONTEST_DIALOG_NAME = "new-contest";
// Mirrors postContestCreateHandler's bound, ref #20260819-contest312.
export const NEW_CONTEST_NAME_MAX_LENGTH = 60;
export const NEW_CONTEST_START_ACTION_LABEL = "Start a contest";
export const NEW_CONTEST_DIALOG_TITLE = "New contest";
export const NEW_CONTEST_DIALOG_DESCRIPTION =
  "Set the dates and rules for this pool's next contest.";
export const NEW_CONTEST_NAME_LABEL = "Contest name";
export const NEW_CONTEST_NAME_REQUIRED_ERROR = "Enter a name for the contest.";
export const NEW_CONTEST_NAME_TOO_LONG_ERROR =
  "Keep the name to 60 characters or fewer.";
export const NEW_CONTEST_NAME_MISSING_ITEM = "a contest name";
export const NEW_CONTEST_SUBMIT_LABEL = "Create contest";
export const NEW_CONTEST_SUBMITTING_LABEL = "Creating your contest";
export const NEW_CONTEST_CANCEL_LABEL = "Cancel";
export const NEW_CONTEST_CLOSE_LABEL = "Close";
export const NEW_CONTEST_LOADING_LABEL = "Loading contest setup";
export const NEW_CONTEST_FAILED_MESSAGE =
  "We could not load this pool's contests.";
export const NEW_CONTEST_RETRY_LABEL = "Try again";
export const NEW_CONTEST_NO_SEASON_BODY =
  "This pool has no season yet. Start one from the Season card in Settings, then set up the contest.";
export const NEW_CONTEST_DISCARD_TITLE = "Discard this contest?";
export const NEW_CONTEST_DISCARD_BODY =
  "You'll lose the dates and rules you've set.";
export const NEW_CONTEST_DISCARD_CONFIRM_LABEL = "Discard";
export const NEW_CONTEST_DISCARD_CANCEL_LABEL = "Keep editing";

// ISSUE-1316. Shown when the form starts from the rules the Commissioner
// picked while creating the pool, before the season's schedule was out.
export const NEW_CONTEST_PENDING_SETUP_NOTE =
  "We kept the rules you picked when you made this pool. Change anything you like.";

export function newContestPrefillNote(contestName: string): string {
  return `We copied the rules from ${contestName}. Dated pick requirements move with the new contest start; review dates and weekdays before creating it.`;
}

export type typeContestListItem = NonNullable<
  NonNullable<ReturnType<typeof useContestList>["data"]>["data"]
>["items"][number];
export type typeNewContestDraft = {
  name: string;
  rules: typeSurvivorRulesDraft;
};
export type typeNewContestSource = { contestId: string; name: string };
export type typeNewContestGate = "loading" | "failed" | "noSeason" | "ready";
export type typeNewContestCreateBody = {
  poolId: string;
  config: typeSurvivorContestConfig;
};
export type typeContestNameValidity =
  | { isValid: true; trimmed: string }
  | { isValid: false; reason: "empty" | "tooLong" };

export function validateContestName(raw: string): typeContestNameValidity {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { isValid: false, reason: "empty" };
  if (trimmed.length > NEW_CONTEST_NAME_MAX_LENGTH) {
    return { isValid: false, reason: "tooLong" };
  }
  return { isValid: true, trimmed };
}

export function contestNameError(
  validity: typeContestNameValidity,
): string | null {
  if (validity.isValid) return null;
  return validity.reason === "empty"
    ? NEW_CONTEST_NAME_REQUIRED_ERROR
    : NEW_CONTEST_NAME_TOO_LONG_ERROR;
}

/**
 * The contest whose rules the form copies: the latest by end date that was not
 * called off. On an end date tie the greater contestId wins, so the pick is
 * stable.
 */
export function pickSourceContest(
  items: readonly typeContestListItem[],
): typeNewContestSource | null {
  let best: typeContestListItem | null = null;
  for (const item of items) {
    if (item.status === VIEWER_CONTEST_STATUS_CALLED_OFF) continue;
    if (
      best === null ||
      item.endDate > best.endDate ||
      (item.endDate === best.endDate && item.contestId > best.contestId)
    ) {
      best = item;
    }
  }
  return best === null ? null : { contestId: best.contestId, name: best.name };
}

export function hasCurrentSeason(
  items: readonly typeSeasonListItem[],
): boolean {
  return items.some((item) => item.isCurrent);
}

/**
 * Owner default A: copy the source contest's rules and start the day after it
 * ended, or today, whichever is later. With no source, the wizard's own start.
 */
export function buildInitialNewContestDraft(input: {
  sourceConfig: typeSurvivorContestConfig | null;
  now: Date;
}): typeNewContestDraft {
  const { sourceConfig } = input;
  if (sourceConfig === null) {
    return { name: "", rules: emptySurvivorRulesDraft(input.now) };
  }
  const today = defaultContestDateRange(input.now).startDate;
  // A TBD source's endDate from /contest/get is its computed window end
  // (ISSUE-1095), so the dates are computed the same way for both modes.
  const dates = nextContestDateRange({
    previousStartDate: sourceConfig.startDate,
    previousEndDate: sourceConfig.endDate ?? "",
    today,
  });
  const rules = rulesDraftFromContestConfig(sourceConfig);
  // Requirements are calendar days in their own named zones. Shift their day
  // labels by the contest start delta, not elapsed hours across DST. Weekday
  // anchors stay intentional; requirement IDs are scoped to the new contest.
  const dayDelta = Math.round(
    (Date.parse(dates.startDate) - Date.parse(sourceConfig.startDate)) /
      86400000,
  );
  const pickSchedule =
    rules.pickSchedule == null
      ? rules.pickSchedule
      : {
          ...rules.pickSchedule,
          requirements: rules.pickSchedule.requirements.map((requirement) => ({
            ...requirement,
            startsOn: addProviderDays({
              day: requirement.startsOn,
              days: dayDelta,
            }),
            endsOn:
              requirement.endsOn === null
                ? null
                : addProviderDays({ day: requirement.endsOn, days: dayDelta }),
          })),
        };
  return {
    name: "",
    rules: {
      ...rules,
      ...(pickSchedule !== undefined ? { pickSchedule } : {}),
      startDate: dates.startDate,
      endDate: rules.endDateMode === "tbd" ? "" : dates.endDate,
    },
  };
}

/**
 * ISSUE-1316. The form starts from the rules saved by the create-pool wizard
 * when the season's schedule was not out yet. Saved dates that are still
 * ahead are kept as picked. Dates that have slipped into the past keep the
 * same contest length but start today, since a contest cannot start in the
 * past.
 */
export type typePendingContestSetup = {
  name: string;
  // The /contest/create config shape, where the end date fields are optional.
  config: Omit<
    typeSurvivorContestConfig,
    | "endDate"
    | "endDateMode"
    | "roundLockTiming"
    | "livesAllowed"
    | "skipsAllowed"
  > & {
    roundLockTiming?: typeSurvivorContestConfig["roundLockTiming"];
    livesAllowed?: number;
    skipsAllowed?: boolean;
    endDate?: string | null;
    endDateMode?: typeSurvivorContestConfig["endDateMode"] | null;
  };
};

export function buildNewContestDraftFromPendingSetup(input: {
  setup: typePendingContestSetup;
  now: Date;
}): typeNewContestDraft {
  const { setup } = input;
  const today = defaultContestDateRange(input.now).startDate;
  const rules = rulesDraftFromContestConfig({
    ...setup.config,
    roundLockTiming: setup.config.roundLockTiming ?? "lastGame",
    livesAllowed: 1,
    skipsAllowed: false,
    endDate: setup.config.endDate ?? null,
    endDateMode: setup.config.endDateMode ?? "date",
  });
  const name = setup.name.slice(0, NEW_CONTEST_NAME_MAX_LENGTH);
  if (setup.config.startDate < today && rules.pickSchedule) {
    const days = Math.round(
      (Date.parse(today) - Date.parse(setup.config.startDate)) / 86400000,
    );
    rules.pickSchedule = {
      ...rules.pickSchedule,
      requirements: rules.pickSchedule.requirements.map((requirement) => ({
        ...requirement,
        startsOn: addProviderDays({ day: requirement.startsOn, days }),
        endsOn:
          requirement.endsOn === null
            ? null
            : addProviderDays({ day: requirement.endsOn, days }),
      })),
    };
  }

  if (setup.config.startDate >= today) {
    return { name, rules };
  }
  if (rules.endDateMode === "tbd") {
    return { name, rules: { ...rules, startDate: today } };
  }
  const counted = countInclusiveDays({
    startDate: setup.config.startDate,
    endDate: setup.config.endDate ?? "",
  });
  const length = counted < 1 ? DEFAULT_SURVIVOR_CONTEST_DAYS : counted;
  return {
    name,
    rules: {
      ...rules,
      startDate: today,
      endDate: addUtcDays(today, length - 1),
    },
  };
}

export function missingNewContestItems(
  draft: typeNewContestDraft,
): readonly string[] {
  return missingSurvivorRulesItems(draft.rules);
}

export function isNewContestDraftDirty(
  draft: typeNewContestDraft,
  baseline: typeNewContestDraft,
): boolean {
  return JSON.stringify(draft.rules) !== JSON.stringify(baseline.rules);
}

export function buildNewContestCreateBody(input: {
  poolId: string;
  draft: typeNewContestDraft;
}): typeNewContestCreateBody | null {
  const config = buildContestConfig(input.draft.rules);
  if (config === null) return null;
  return { poolId: input.poolId, config };
}

export function resolveNewContestGate(input: {
  isLoading: boolean;
  hasFailed: boolean;
  hasCurrentSeason: boolean;
}): typeNewContestGate {
  if (input.hasFailed) return "failed";
  if (input.isLoading) return "loading";
  if (!input.hasCurrentSeason) return "noSeason";
  return "ready";
}
